// Company members (fleet-4, sql/fleet-4.sql): more than one login per management company. Each member has rights per AREA and per
// vessel; the database decides (my_perm -> hc_member_lvl), this file only mirrors it for the screens. Plain JS: tests/fleet-4.test.mjs
// checks the module lists against the SQL so the two never drift.
import { PERM_MODULES } from './permModules.js'

export const AREAS = [
  { key: 'approvals', label: 'Approvals', levels: ['none', 'read', 'full'], words: { none: 'Hidden', read: 'View', full: 'Approve' },
    help: 'Purchase orders and APA close requests, as management. Approve = up to the limit below.' },
  { key: 'compliance', label: 'Compliance', levels: ['none', 'read', 'write', 'full'], words: { none: 'Hidden', read: 'View', write: 'Edit', full: 'Full (DPA)' },
    help: 'Certificates, checklists & drills, LSA, risk assessments, permits, ISM, manuals, hours of rest. Full = closes ISM findings as management.' },
  { key: 'budget', label: 'Budget & reports', levels: ['none', 'read', 'write'], words: { none: 'Hidden', read: 'View', write: 'Edit' },
    help: 'Budget, reports and the fleet report; View on purchase orders and APA.' },
  { key: 'standards', label: 'Company standards', levels: ['none', 'read', 'full'], words: { none: 'No', read: 'View', full: 'Manage' },
    help: 'Manage = save a standard from a vessel and send it to the vessels.' },
  { key: 'vessel', label: 'Vessel records', levels: ['none', 'read', 'write'], words: { none: 'Hidden', read: 'View', write: 'Edit' },
    help: 'Everything else on board: maintenance, equipment, inventory, crew, logs, To Do, voyage.' },
]
export const DEFAULT_RIGHTS = { approvals: 'none', approval_limit: null, approval_currency: 'EUR', compliance: 'read', budget: 'none', standards: 'none', vessel: 'read' }

// the four usual people; the owner can change any right after picking one
export const TITLES = [
  { key: 'DPA', label: 'DPA', rights: { approvals: 'none', compliance: 'full', budget: 'none', standards: 'full', vessel: 'read' } },
  { key: 'Technical superintendent', label: 'Technical superintendent', rights: { approvals: 'full', approval_limit: 5000, compliance: 'write', budget: 'read', standards: 'read', vessel: 'write' } },
  { key: 'Accounts', label: 'Accounts', rights: { approvals: 'read', compliance: 'none', budget: 'write', standards: 'none', vessel: 'none' } },
  { key: 'Fleet manager', label: 'Fleet manager', rights: { approvals: 'full', approval_limit: null, compliance: 'full', budget: 'write', standards: 'full', vessel: 'write' } },
]
export const titleRights = key => cleanRights((TITLES.find(t => t.key === key) || {}).rights)

// the module -> area map of hc_member_lvl (sql/fleet-4.sql) - keep the two the same
export const AREA_MODULES = {
  approvals: ['purchase_orders', 'apa'],
  budget: ['budget', 'reports'],
  compliance: ['risk', 'manuals', 'checklists', 'documents', 'lsa', 'ptw', 'ism_audits', 'regulations', 'officer_review', 'hours_rest'],
  always: ['dashboard', 'ai', 'calendar'],
}
const LV = { none: 0, read: 1, write: 2, full: 3 }
const NAME = ['none', 'read', 'write', 'full']

// hc_member_clean: every area with an allowed level
export function cleanRights(r) {
  const x = r && typeof r === 'object' ? r : {}
  const out = {}
  for (const a of AREAS) out[a.key] = a.levels.includes(x[a.key]) ? x[a.key] : DEFAULT_RIGHTS[a.key]
  const lim = typeof x.approval_limit === 'string' ? (/^\s*\d+(\.\d+)?\s*$/.test(x.approval_limit) ? Number(x.approval_limit) : null) : x.approval_limit
  out.approval_limit = Number.isFinite(lim) && lim >= 0 ? Math.round(lim * 100) / 100 : null
  const cur = String(x.approval_currency || 'EUR').trim().toUpperCase()
  out.approval_currency = /^[A-Z]{3}$/.test(cur) ? cur : 'EUR'
  return out
}

// hc_member_lvl: the level (0-3) of a member on one module
export function memberLevel(rights, module) {
  const r = cleanRights(rights)
  const ap = LV[r.approvals], co = LV[r.compliance], bu = LV[r.budget], st = LV[r.standards], ve = LV[r.vessel]
  if (AREA_MODULES.approvals.includes(module)) return Math.max(ap, Math.min(bu, 1))
  if (AREA_MODULES.budget.includes(module)) return bu
  if (['risk', 'manuals', 'checklists'].includes(module)) return Math.max(co, Math.min(st, 1))
  if (AREA_MODULES.compliance.includes(module)) return co
  if (module === 'crew_personal') return Math.min(co, 1)
  if (module === 'audit') return 3
  if (AREA_MODULES.always.includes(module)) return Math.max(ve, 1)
  return ve
}
// the permissions object a crew login has (module -> 'none' | 'read' | 'write' | 'full'), for can() and the pages
export function memberPermissions(rights) {
  const out = {}
  for (const [k] of PERM_MODULES) out[k] = NAME[memberLevel(rights, k)]
  out.crew_personal = NAME[memberLevel(rights, 'crew_personal')]
  return out
}
export const memberCan = (member, module, level = 'read') => !member || memberLevel(member.rights, module) >= LV[level]
export const inScope = (member, vesselId) => !member || !member.vessel_ids || member.vessel_ids.map(String).includes(String(vesselId))

const money = (n, cur) => Number(n).toLocaleString('en-GB', { maximumFractionDigits: 0 }) + ' ' + cur
export function limitText(rights) {
  const r = cleanRights(rights)
  if (r.approvals !== 'full') return null
  return r.approval_limit == null ? 'no limit' : 'up to ' + money(r.approval_limit, r.approval_currency)
}
// "Approve up to 5,000 EUR · Compliance Edit · Vessel records Edit" - the team list's one line
export function rightsSummary(rights) {
  const r = cleanRights(rights)
  return AREAS.filter(a => r[a.key] !== 'none').map(a => a.key === 'approvals' && r.approvals === 'full' ? 'Approve ' + limitText(r) : a.label + ' ' + a.words[r[a.key]]).join(' · ') || 'No access'
}
export const vesselsText = (member, fleet) => {
  if (!member.vessel_ids) return 'All vessels'
  const names = member.vessel_ids.map(id => (fleet.find(v => String(v.id) === String(id)) || {}).vessel_name).filter(Boolean)
  return names.length ? names.join(', ') : 'No vessel'
}
